import { Store } from 'pullstate';
import { Grouping } from '../types/common';

import { PagedList } from 'src/types/common';
import { bool, number } from 'yup';
import { boolean } from 'yup/lib/locale';

export type Industry = {
    id: string;
    name: string;
};
export type Country = {
    id: string;
    name: string;
};

export type LeadLevel = {
    id: string;
    name: string;
};

export type LeadStage = {
    id: string;
    name: string;
    stageColor: string;
};

export type Account = {
    id: string;
    name: string;
    revenue: Revenue;
    employeeSize: EmployeeSize;
};

export type Revenue = {
    id: string;
    name: string;
};

export type EmployeeSize = {
    id: string;
    name: string;
};

export type ActivityType = {
    id: string;
    name: string;
    icon: string;
};

export type User = {
    id: string;
    fullName: string;
    avatarFileName: string;
};

export type LeadFunction = {
    id: string;
    name: string;
};

export type LeadSource = {
    id: string;
    name: string;
};
export type AddressRegion = {
    id: string;
    name: string;
};

export type Sprint = {
    id: string;
    name: string;
};

export type Lead = {
    id: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    industry: Industry;
    country: Country;
    fullName: string;
    jobTitle: string;
    company: string;
    directPhone: string;
    mobilePhone: string;
    switchboardPhone: string;
    phoneExtension: string;
    addressStreet: string;
    addressCity: string;
    addressProvince: string;
    addressPostalCode: string;
    website: string;
    linkedIn: string;
    twitter: string;
    salutation: string;
    leadLevel: LeadLevel;
    leadStage: LeadStage;
    account: Account;
    hotLead: boolean;
    meaningfulInteraction: boolean;
    lastActivityDateTime: string;
    lastActivity: ActivityType;
    lastActivityOwner: User;
    nextActivityDateTime: string;
    nextActivity: ActivityType;
    nextActivityOwner: User;
    leadFunction: LeadFunction;
    leadSource: LeadSource;
    addressRegion: AddressRegion;
    fax: string;
    sprint: Sprint;
    owner: User;
    nextActivityCadenceStep: CadenceStep;
    switchboardEmail: string;
    customer: Customer;
    dncPhone: boolean;
    dncMobile: boolean;
    dncSwitchboard: boolean;
    referralFrom: ReferralLead;
    referralTo: ReferralLead;
    [key: string]: any;
};

export type MissingData = {
    LabelText: string;
    ValueText: string;
    ValueColor: string;
    ErrorText: string;
    mi: boolean;
    [key: string]: any;
};

export type ActivityOutcome = {
    id: string;
    name: string;
    icon: string;
    isMeaningfull: boolean;
    buttonColor: string;
};

export type SprintType = {
    id: string;
    name: string;
};

export type Customer = {
    id: string;
    name: string;
};

export type ReferralLead = {
    id: string;
    fullName: string;
};

export type Cadence = {
    id: string;
    name: string;
};

export type CadenceStep = {
    id: string;
    processOrder: number;
    playbookCadence: Cadence;
};

export type SocialMediaPlatform = {
    id: string;
    name: string;
};

export type DiscoveryCallAttendees = {
    id: string;
    fullName: string;
    email: string;
    jobTitle: string;
};

export type LeadActivity = {
    id: string;
    leadId: string;
    created: string;
    createdBy: string;
    currentActivity: ActivityType;
    currentActivityOwnerName: string;
    currentActivityDate: string;
    currentActivityOutcome: ActivityOutcome;
    sprintName: string;
    currentCadenceStep: string;
    currentCadenceName: string;
    currentStage: LeadStage;
    nextStage: LeadStage;
    nextActivity: ActivityType;
    nextActivityDate: string;
    nextCadenceStep: string;
    nextCadenceName: string;
    notes: string;
    callSid: string;
    callCost: string;
    callStartTime: string;
    durationSeconds: string;
    callStatus: string;
    phoneTo: string;
    phoneFrom: string;
    recording: string;
    recordingSid: string;
    emailFrom: string;
    emailTo: string;
    emailCC: string;
    emailSubject: string;
    emailBody: string;
    socialMediaUsername: string;
    socialMediaPlatform: string;
    isAction: boolean;
    actionActivityName: string;
    taskOwnerName: string;
    taskDueDate: string;
    taskDetails: string;
    taskStatusName: string;
    taskCreatedByName: string;
    taskStatusColor: string;
    noteDetails: string;
    newOwnerName: string;
    newSprintName: string;
    newCedenceName: string;
    newActivityName: string;
    discoveryCallAttendees: DiscoveryCallAttendees[];
    currentActivitySubOutcomeName: string;
    currentActivitySubOutcomeColor: string;
    actionActivityDate: string;
    referralToLeadName: string;
    referralToLeadId: string;
    referralFromLeadName: string;
    referralFromLeadId: string;
    [key: string]: any;
};

export type NextActivity = {
    name: string;
    date: string;
    owner: string;
    icon: string;
    hasRecord: boolean;
    [key: string]: any;
};

export type TaskList = {
    id: string;
    ownerName: string;
    ownerId: string;
    leadName: string;
    leadId: string;
    details: string;
    statusName: string;
    statusId: string;
    statusColor: string;
    createdDate: string;
    dueDate: string;
    createdBy: string;
    currentUserId: string;
    lead: Lead;
};

export type LeadNote = {
    id: string;
    created: string;
    createdBy: string;
    notes: string;
};

export type LeadFilterOptions = {
    countries: string[];
    industries: string[];
    [key: string]: string[] | string | null | undefined;
};

export const enum ViewType {
    None = 0,
    AccountDetails = 1,
    ActivityTimeline = 2,
    Notes = 3,
    Edit = 4,
    ActivityExecution = 5,
    EditLead = 6,
    DoActivity = 7,
    ViewCadence = 8,
    ViewSprint = 9,
    ViewCompany = 10,
    Tasks = 11,
}

export const enum GroupType {
    None = 0,
    Country = 1,
    Industry = 2,
}

export const LeadScreenStore = new Store({
    selectedLead: null as null | Lead,
    selectedLeadCard: null as null | Lead,
    referralLead: null as null | Lead,
    selectedView: ViewType.None,
    loadNextLead: false,
    closeOnNoMoreLeads: false,
    loadPreviousLead: false,
    fetchedNext: false,
    refreshLeads: false,
    isNewPopup: false,
    isAddReferralPopup: false,
    groupBy: GroupType.None,
    editMode: false,
    filters: {
        countries: [],
        industries: [],
    } as LeadFilterOptions,
});

export const LeadListStore = new Store({
    list: {
        hasNextPage: false,
        pageNumber: 0,
        totalCount: 0,
        totalPages: 0,
        pageSize: 10,
        items: [] as Lead[],
    } as PagedList<Lead>,
});

export const ActivityScreenStore = new Store({
    selectedActivity: null as null | LeadActivity,
    selectedView: ViewType.None,
    editMode: false,
    openActivity: false,
});

export const LeadActivityStore = new Store({
    items: [] as LeadActivity[],
});

export const LeadAccountStore = new Store({
    record: {
        accountLeads: [] as AccountLeads[],
        sprintLeads: [] as AccountLeads[],
        leadCount: 0,
        leadSprintCount: 0,
        connectionRate: 0,
        accountName: 'Not Assigned',
        industryName: '',
        hqAddress: '',
        hqCity: '',
        hqProvince: '',
        hqPostalCode: '',
        hqCountryName: '',
        hqRegionName: '',
        revenueAmount: '',
        logo: '',
        employeeSize: '',
        accountStatusName: '',
        doNotContact: false,
    },
});

export const LeadCompanyStore = new Store({
    record: {
        companyLeads: [] as AccountLeads[],
        sprintLeads: [] as AccountLeads[],
        leadCount: 0,
        leadSprintCount: 0,
        connectionRate: 0,
        companyName: 'Not Assigned',
    },
});

export const LeadSprintStore = new Store({
    record: {
        sprintLeads: [] as AccountLeads[],
        leadCount: 0,
        connectionRate: 0,
        sprintName: 'Not Assigned',
        sprintType: '',
        sprintStatus: '',
        customerName: '',
        startDate: '',
        endDate: '',
        playbookName: '',
    },
});

export type AccountLeads = {
    leadId: string;
    leadName: string;
    jobTitle: string;
    company: string;
    hasMI: boolean;
    isHot: boolean;
    leadOwnerName: string;
    sprintId: string;
    sprintName: string;
    country: string;
    stageName: string;
    stageColor: string;
};

export const LeadActivityHistoryStore = new Store({
    record: {
        activities: [] as LeadActivityHistory[],
        miActivities: [] as LeadActivityHistory[],
        activityCount: 0,
        connectionRate: 0,
        lastActivityname: '',
        lastActivityDateTime: '',
        lastActivityOwnerName: '',
        miCount: 0,
        lastActivityOutcomeName: '',
    },
});

export type LeadActivityHistory = {
    leadId: string;
    created: string;
    createdBy: string;
    currentActivityName: string;
    currentActivityOwnerName: string;
    currentActivityDate: string;
    currentActivityOutcomeName: string;
    currentActivityHasMi: boolean;
    currentActivityOutcomeButtonColor: string;
    currentActivityOutcomeIcon: string;
    sprintName: string;
    currentCadenceName: string;
    currentCadenceStep: string;
    currentStageName: string;
    currentStageColor: string;
    nextStageName: string;
    nextStageColor: string;
    nextActivityName: string;
    nextActivityDate: string;
    nextCadenceName: string;
    nextCadenceStep: string;
    notes: string;
    callSid: string;
    callStatus: string;
    callStartTime: string;
    callCost: string;
    durationSeconds: string;
    phoneTo: string;
    phoneFrom: string;
    recording: string;
    recordingSid: string;
    emailTo: string;
    emailFrom: string;
    emailCC: string;
    emailSubject: string;
    emailBody: string;
    socialMediaUsername: string;
    socialMediaPlatform: string;

    [key: string]: any;
};

export const ActivityDialogStore = new Store({
    accountLeadMode: false,
    sprintLeadMode: false,
    editLeadMode: false,
});

export const CompanyDialogStore = new Store({
    companyMode: false,
    sprintMode: false,
});

export const SprintDialogStore = new Store({
    sprintDialogMode: false,
});

export const ActivityHistoryDialogStore = new Store({
    activityHistorDialogMode: false,
    miHistorDialogMode: false,
});

export const HistoryDetailsDialogStore = new Store({
    historyDetailsDialogStore: false,
    activityId: '',
});

export const LeadSingleActivityStore = new Store({
    record: {
        activityId: '',
        leadId: '',
        createdDate: '',
        createdBy: '',
        currentActivityName: '',
        currentActivityOwnerName: '',
        currentActivityDate: '',
        currentActivityOutcomeName: '',
        currentActivityOutcomeButtonColor: '',
        sprintName: '',
        currentCadenceName: '',
        currentCadenceStep: '',
        currentStageName: '',
        currentStageColor: '',
        nextStageName: '',
        nextStageColor: '',
        nextActivityName: '',
        nextActivityDate: '',
        nextCadenceName: '',
        nextCadenceStep: '',
        notes: '',
        callSid: '',
        callStatus: '',
        callStartTime: '',
        callCost: '',
        durationSeconds: '',
        phoneTo: '',
        phoneFrom: '',
        recording: '',
        recordingSid: '',
        emailTo: '',
        emailFrom: '',
        emailCC: '',
        emailSubject: '',
        emailBody: '',
        socialMediaUsername: '',
        socialMediaPlatform: '',
        isAction: false,
        actionActivityName: '',
        taskOwnerName: '',
        taskDueDate: new Date().toISOString(),
        taskDetails: '',
        taskStatusName: '',
        taskCreatedByName: '',
        taskStatusColor: '',
        noteDetails: '',
        newOwnerName: '',
        newSprintName: '',
        newCedenceName: '',
        newActivityName: '',
        discoveryCallAttendees: [] as DiscoveryCallAttendees[],
        currentActivitySubOutcomeName: '',
        currentActivitySubOutcomeColor: '',
        actionActivityDate: new Date().toISOString(),
        referralToLeadName: '',
        referralToLeadId: '',
        referralFromLeadName: '',
        referralFromLeadId: '',
    },
});

export type PlaybookLibraries = {
    name: string;
    content: string;
    [key: string]: any;
};

export const LeadActivityMessagingStore = new Store({
    record: {
        templateId: '',
        templateName: '',
        templateSubject: '',
        templateBody: '',
        templateEmailTo: '',
        templateTypeName: '',
        playbookLibraries: [] as PlaybookLibraries[],
    },
});

export type PlaybookCadenceSteps = {
    processOrder: string;
    currentActivityName: string;
    proccessWhen: number;
    executedCount: number;
    mICount: number;
    currentActivityIcon: string;
    connectionRate: number;
    [key: string]: any;
};

export const LeadActivityCadenceStore = new Store({
    record: {
        name: '',
        cadenceTypeName: '',
        startStageName: '',
        endStageName: '',
        activeStartDate: '',
        activeEndDate: '',
        endAction: '',
        cadenceTemplateName: '',
        nextCadenceName: '',
        currentStepProcessOrder: 0,
        currentStepActivityName: '',
        currentStepExecutedCount: 0,
        currentStepMICount: 0,
        currentStepCount: 0,
        connectionRate: 0,
        playbookCadenceSteps: [] as PlaybookCadenceSteps[],
    },
});

export const CadenceListDialogStore = new Store({
    cadenceListDialogMode: false,
});

export type ActivityOutcomes = {
    outcomeId: string;
    name: string;
    orderBy: number;
    icon: string;
    buttonColor: string;
    tooltip: string;
    isSelected: boolean;
    autoSent: boolean;
    autoNotSent: boolean;
    outcomeGroupId: string;
    activitySubOutcomes: ActivitySubOutcomes[];
    [key: string]: any;
};

export type ActivitySubOutcomes = {
    id: string;
    name: string;
    orderBy: number;
    icon: string;
    buttonColor: string;
    tooltip: string;
    isSelected: boolean;
    autoSent: boolean;
    autoNotSent: boolean;
    activityOutcomeId: string;
    [key: string]: any;
};

export type ActivityOutcomeGroups = {
    outcomeGroupId: string;
    name: string;
    orderBy: number;
    icon: string;
    buttonColor: string;
    tooltip: string;
    [key: string]: any;
};

export type UserPhoneNumbers = {
    sid: string;
    friendlyName: string;
    number: string;
    location: string;
    countryName: string;
    [key: string]: any;
};

export type LeadPhoneNumbers = {
    number: string;
    numberType: string;
    numberIcon: string;
    [key: string]: any;
};

export const LeadActivityOutcomeStore = new Store({
    record: {
        leadId: '',
        selectedId: '',
        selectedSubId: '',
        activityNotes: '',
        activityTypeName: '',
        phoneTo: '',
        phoneFrom: '',
        emailTo: '',
        emailFrom: '',
        emailCC: '',
        emailSubject: '',
        emailBody: '',
        socialMediaBody: '',
        socialMediaUsername: '',
        hotActivity: false,
        callQuality: '',
        mobilePhone: '',
        directPhone: '',
        switchboardPhone: '',
        hasEmailFromError: false,
        smsBody: '',
        saveDisabled: true,
        autoSentId: '',
        autoNotSentId: '',
        hasGroup: false,
        hasOutcomes: false,
        showNotesPopup: false,
        configurationIssue: false,
        userId: '',
        traceLevel: '' as 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'silent',
        callRecord: 'false',
        actionResult: '',
        actionId: '',
        userCustomerDetailsId: '',
        switchboardEmail: '',
        hasTwoEmails: false,
        noLeadNumbers: false,
        discoveryCallDate: '',
        nextActivityDate: '',
        nextActivityId: '',
        roomName: '',
        organizationId: '',
        userPhoneNumbers: [] as UserPhoneNumbers[],
        activityOutcomes: [] as ActivityOutcomes[],
        activitySubOutcomes: [] as ActivitySubOutcomes[],
        leadPhoneNumbers: [] as LeadPhoneNumbers[],
        activityOutcomeGroups: [] as ActivityOutcomeGroups[],
    },
});

export const ActivityNotesDialogStore = new Store({
    activityNotesDialogMode: false,
});

export const HotLeadStore = new Store({
    record: {
        leadId: '',
        hotLead: false,
    },
});

export type CadenceResponse = {
    cadenceId: string;
    cadenceStepId: string;
    cadenceName: string;
    startStageName: string;
    processOrder: number;
    activityName: string;
    cadenceType: string;
    [key: string]: any;
};

export const CadenceListStore = new Store({
    record: {
        playbookCadences: [] as CadenceResponse[],
    },
});

export const CadenceSelectionDialogStore = new Store({
    cadenceSelectionDialogMode: false,
});

export const TwilioStore = new Store({
    record: {
        token: '',
        identity: '',
    },
});

export const CallButton = new Store({
    showCallButton: true,
    showHangupButton: false,
    showOtherButtons: false,
    showCallingButton: false,
});

export const DailpadPopupStore = new Store({
    dialpadDialogMode: false,
});

export const LeadActivitySMSStore = new Store({
    record: {
        leadId: '',
        smsBody: '',
        phoneTo: '',
        phoneFrom: '',
    },
});

export const LeadActivityEmailStore = new Store({
    record: {
        leadId: '',
        emailBody: '',
        emailTo: '',
        emailFrom: '',
        emailSubject: '',
        userCustomerDetailsId: '',
    },
});

export const IncomingCallPopupStore = new Store({
    incomingCallDialogMode: false,
    numberCalling: '',
    showAnswerButton: false,
    showHungupButton: false,
    showRejectButton: false,
    showCloseButton: false,
    showSelectButton: false,
    searchString: 'DONOTSEARCH',
});

export const LeadSearchStore = new Store({
    record: {
        matchedLeads: [] as AccountLeads[],
        leadCount: 0,
    },
});

export const InboundCallStore = new Store({
    showPhoneControls: false,
    hasHungup: false,
});

export const QuickLeadDialogStore = new Store({
    quickLeadDialogMode: false,
});

export const DashboardTodoStore = new Store({
    record: {
        yesterdayLeadCount: 0,
        overdueLeadCount: 0,
        todayLeadCount: 0,
        tomorrowLeadCount: 0,
        thisWeekLeadCount: 0,
        thisMonthLeadCount: 0,
        outboundEmailLeadCount: 0,
        outboundPhoneCallLeadCount: 0,
        outboundSMSLeadCount: 0,
        outboundSocialMediaLeadCount: 0,
        outboundWhatsAppLeadCount: 0,
        inboundPhoneCallLeadCount: 0,
        inboundSMSLeadCount: 0,
        inboundWhatsAppLeadCount: 0,
        inboundEmailLeadCount: 0,
        inboundSocialMediaLeadCount: 0,
        incomingVoicemails: 0,
        openTasksCount: 0,
        hotLeadCount: 0,
        discoveryCallCount: 0,
    },
});

export const DashboardViewStore = new Store({
    showDefault: true,
    showDetail: false,
    showTodoList: true,
    showActivityWorkList: false,
    showInboundCallList: false,
    showPerformanceToday: true,
    showPerformanceThisWeek: false,
    showPerformanceThisMonth: false,
    showKPIPerformance: true,
    showActivityHistoryList: false,
    showTaskList: true,
    activityDate: '',
    activityWorkListTitle: '',
    activityName: '',
    customerId: '',
    activityType: '',
    activityHistoryTitle: '',
});

export const InboundCallsStore = new Store({
    items: [] as InboundCall[],
});

export type InboundCall = {
    id: string;
    callDate: string;
    status: string;
    incomingVMUrl: string;
    vmDuration: string;
    fromNumber: string;
    fromState: string;
    leadName: string;
    leadId: string;
    lead: Lead;
    [key: string]: any;
};

export const ActivitySummaryDashboardResponse = new Store({
    record: {
        dateRange: '',
        totalActivities: 0,
        totalMeaningfulActivities: 0,
        totalMqlActivities: 0,
        totalDiscoveryCalls: 0,
        totalOpportunityActivities: 0,
        totalActivitiesDifference: 0,
        totalMeaningfulDifference: 0,
        totalMqlDifference: 0,
        totalDiscoveryCallDifference: 0,
        totalOpportunityDifference: 0,
        comparedTo: '',
        totalActivitiesDif: '',
        totalMIActivitiesDif: '',
        totalMQLActivitiesDif: '',
        totalDCActivitiesDif: '',
        TotalOpportunityActivitiesDif: '',
    },
});

export type DashboardKPIs = {
    customerId: string;
    customerName: string;
    activityKPI: number;
    activityCount: number;
    difActivity: string;
    activityDifCount: number;
    mikpi: number;
    miCount: number;
    difMI: string;
    miDifCount: number;
    mqlkpi: number;
    mqlCount: number;
    difMQL: string;
    mqlDifCount: number;
    dckpi: number;
    dcCount: number;
    difDC: string;
    dcDifCount: number;
    activityProgressPercent: number;
    miProgressPercent: number;
    mqlProgressPercent: number;
    dcProgressPercent: number;
    oppKPI: number;
    oppCount: number;
    difOpp: string;
    oppDifCount: number;
    oppProgressPercent: number;
};

export const KPISummaryDashboardResponse = new Store({
    record: {
        customerCount: 0,
        selectedCustomer: '',
        kpiDetails: [] as DashboardKPIs[],
    },
});

export type LeadActivityDashboard = {
    Id: string;
    leadId: string;
    createdDate: string;
    createdBy: string;
    currentActivityName: string;
    currentActivityOwnerName: string;
    currentActivityDate: string;
    currentActivityOutcomeName: string;
    currentActivityOutcomeButtonColor: string;
    sprintName: string;
    currentCadenceName: string;
    currentCadenceStep: string;
    currentStageName: string;
    currentStageColor: string;
    nextStageName: string;
    nextStageColor: string;
    nextActivityName: string;
    nextActivityDate: string;
    nextCadenceName: string;
    nextCadenceStep: string;
    notes: string;
    callSid: string;
    callStatus: string;
    callStartTime: string;
    callCost: string;
    durationSeconds: string;
    phoneTo: string;
    phoneFrom: string;
    recording: string;
    recordingSid: string;
    emailTo: string;
    emailFrom: string;
    emailCC: string;
    emailSubject: string;
    emailBody: string;
    socialMediaUsername: string;
    socialMediaPlatform: string;
    leadFullName: string;
    smsBody: string;
    socialMediaBody: string;
    lead: Lead;
    hasMI: boolean;
    hasMQL: boolean;
    hasDC: boolean;
    hasOpportunity: boolean;
    [key: string]: any;
};

export const LeadActivityDashboardStore = new Store({
    items: [] as LeadActivityDashboard[],
});

export const ActivityDashboardDialogStore = new Store({
    notesDiealogMode: false,
    emailBodyDiealogMode: false,
    smsBodyDiealogMode: false,
    socialMediaDiealogMode: false,
    notesText: '',
    emailBodyText: '',
    smsBodyText: '',
    socialMediaText: '',
});

export const LeadOverrideStore = new Store({
    changeActivityMode: false,
    changeCadenceMode: false,
    changeSprintMode: false,
    changeOwnerMode: false,
    changeDateMode: false,
});

export const ChangeActivityStore = new Store({
    leadId: '',
    activityTypeId: '',
});

export const ChangeCadenceStore = new Store({
    leadId: '',
    playbookCadenceId: '',
});

export const ChangeSprintStore = new Store({
    leadId: '',
    sprintId: '',
});

export const ChangeOwnerStore = new Store({
    leadId: '',
    userId: '',
});

export const ChangeDateStore = new Store({
    leadId: '',
    activityDate: '',
});

export const NewTaskStore = new Store({
    leadId: '',
    ownerId: '',
    dueDate: '',
    details: '',
});

export const CompletedTaskStore = new Store({
    hasChanged: false,
});

export const NotesTasksStore = new Store({
    record: {
        noteCount: 0,
        taskCount: 0,
    },
});

export const NotesListDialogStore = new Store({
    notesListDialogMode: false,
    addMode: false,
});

export const TaskListDialogStore = new Store({
    taskListDialogMode: false,
    addMode: false,
});

export const FilterSingleLead = new Store({
    filterSingleLead: false,
});

export const DeleteLeadStore = new Store({
    deleteLead: false,
});

export type DiscoveryCallAttendee = {
    id: string;
    fullName: string;
    emailAddress: string;
    jobTitle: string;
    [key: string]: any;
};

export const DiscoveryCallAttendeesStore = new Store({
    items: [] as DiscoveryCallAttendee[],
});

export const ReferralSearchStore = new Store({
    referralFromId: '',
    selectedLeadId: '',
});

export const ActivityTemplateSelectionStore = new Store({
    activityTemplateSelectionMode: false,
});

export type PlaybookTemplate = {
    id: string;
    name: string;
    emailTo: string;
    subject: number;
    body: string;
    deliverTo: string;
    [key: string]: any;
};

export const LeadActivityTemplateStore = new Store({
    record: {
        playbookTemplates: [] as PlaybookTemplate[],
        playbookTemplateVariants: [] as PlaybookTemplate[],
    },
});
